import classNames from 'classnames';
import styles from './BlurbCard.module.scss';
import { Heading, GridCell, Button, LinkButton } from 'ui/components';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';

export interface BlurbCardProps extends Models.BlurbCard {
	variant?: 'default' | 'website';
	dictionary?: { [key: string]: string };
	style?: 'default' | 'bordered';
	language?: string;
	location?: Models.AcademyLocation;
	academyCardType?: Models.AcademyType['name'] | string;
	handleButtonClick?: () => void;
	localizedDate?: string;
}

export const BlurbCard: React.FC<BlurbCardProps> = ({
	style = 'default',
	link,
	className,
	heading,
	kicker,
	text,
	settings,
	date,
	variant,
	language,
	location,
	dictionary,
	academyCardType,
	handleButtonClick,
	localizedDate,
}) => {
	const { desktopWidth, mobileWidth } = settings?.properties ?? { desktopWidth: '100', mobileWidth: '100' };
	const rawDateString = typeof date === 'string' ? date : date?.toLocaleDateString();
	const showDate = date || localizedDate;

	return (
		<GridCell
			desktopWidth={desktopWidth}
			mobileWidth={mobileWidth}
			className={classNames(
				styles.BlurbCard,
				{ [styles[`BlurbCard___${style}`]]: style },
				{ [styles.BlurbCard___website]: variant === 'website' },
				{ [styles[`BlurbCard___${academyCardType}`]]: academyCardType },
				className,
			)}
		>
			<div className={styles.BlurbCard_contentsWrapper}>
				{variant === 'default' && (
					<>
						<a className={styles.BlurbCard_link} href={link?.url} target={link?.target} title={link?.name}>
							<div className={styles.BlurbCard_textbox}>
								{kicker && <div className={styles.BlurbCard_kicker}>{kicker}</div>}
								<Heading headingLevel="h3" className={styles.BlurbCard_heading}>
									{heading}
								</Heading>
								{date && <div className={styles.BlurbCard_date}>{new Date(date).toLocaleDateString()}</div>}
								<div className={styles.BlurbCard_text} dangerouslySetInnerHTML={{ __html: text }}></div>
							</div>
						</a>
					</>
				)}
				{variant === 'website' && (
					<>
						{showDate && (
							<div className={styles.BlurbCard_date}>
								<time dateTime={rawDateString}>{localizedDate}</time>
							</div>
						)}
						<div className={classNames(styles.BlurbCard_contentsContainer)}>
							<div className={styles.BlurbCard_textbox}>
								<div className={styles.BlurbCard_information}>
									{kicker && <p className={styles.BlurbCard_kicker}>{kicker}</p>}
									<Heading headingLevel="h5" className={styles.BlurbCard_heading}>
										{heading}
									</Heading>
									<div className={styles.BlurbCard_text} dangerouslySetInnerHTML={{ __html: text }}></div>
								</div>
								{language && (
									<div className={styles.BlurbCard_metaData}>
										<p className={classNames(styles.BlurbCard_label, styles.BlurbCard_languageLabel)}>
											{dictionary?.cardLanguageLabel}
										</p>
										<p className={styles.BlurbCard_languageText}>{language}</p>
									</div>
								)}
								{location && (
									<div className={styles.BlurbCard_metaData}>
										<p className={classNames(styles.BlurbCard_label, styles.BlurbCard_addressLabel)}>
											{dictionary?.cardAddressLabel}
										</p>
										{location.name && <p className={styles.BlurbCard_addressText}>{location.name}</p>}
										{location.address && <p className={styles.BlurbCard_addressText}>{location.address}</p>}
									</div>
								)}
							</div>
							{link ? (
								<LinkButton
									className={styles.BlurbCard_button}
									style="secondaryWeb"
									withBackIcon={<IconArrowRight />}
									url={link?.url}
									target={link?.target}
									title={`${dictionary?.cardButtonText} ${heading}`}
									ariaLabel={`${dictionary?.cardButtonText} ${heading}`}
								>
									{dictionary?.cardButtonText}
								</LinkButton>
							) : (
								<Button
									className={styles.BlurbCard_button}
									style="secondaryWeb"
									withBackIcon={<IconArrowRight />}
									onClick={handleButtonClick}
									title={`${dictionary?.cardButtonText} ${heading}`}
									ariaLabel={`${dictionary?.cardButtonText} ${heading}`}
								>
									{dictionary?.cardButtonText}
								</Button>
							)}
						</div>
					</>
				)}
			</div>
		</GridCell>
	);
};
